import { IBatchjob } from "@mctypes/batchjob.types";
import { Alert, Button, Paper, Select, Stack, Title } from "@mantine/core";
import React from "react";
import { SegmentedToggle, SwitchWithTitle, VSpace } from "..";
import CheckOrWarn from "../CheckOrWarn";
import { IfcVersionEnums, RevitIfcParamsEnums } from "../../mc-shared/sharedConstants";

const RevitToIfcSettings: React.FC<{
  revitIfcSettings: IBatchjob.IRevitToIfcSettings;
  saveJobParams: (value, key: string) => void;
  savePropertyMapping: (mapping: IBatchjob.IPropertyMapping) => void;
  isValid: boolean;
}> = ({ revitIfcSettings, saveJobParams, savePropertyMapping, isValid }) => {
  const [activeTab, setActiveTab] = React.useState<"GENERAL" | "PROPERTYMAPPING">("GENERAL");
  return (
    <Paper withBorder p="sm" radius="sm" bg="#fdfdff">
      <Stack gap="xs">
        <Title order={4} fw={400}>
          <CheckOrWarn bool={isValid} />
          Revit til IFC-innstillinger
        </Title>
        <Title order={6} fw={400} c="dimmed">
          Spesifiser hvilke innstillinger i Revit som skal brukes for IFC-eksporten
        </Title>
        <div style={{ width: "340px", maxWidth: "100%" }}>
          <SegmentedToggle
            value={activeTab}
            onChange={(tab) => setActiveTab(tab as "GENERAL" | "PROPERTYMAPPING")}
            options={[
              { value: "GENERAL", label: "Generelt" },
              { value: "PROPERTYMAPPING", label: "Property mapping" },
            ]}
          />
        </div>
        <VSpace />
        {activeTab === "GENERAL" && revitIfcSettings?.jobParams != null && (
          <JobParamsSettings jobParams={revitIfcSettings.jobParams} save={saveJobParams} />
        )}
        {activeTab === "PROPERTYMAPPING" && (
          <PropertyMappingSettings
            propertyMapping={revitIfcSettings.propertyMapping}
            saveProperyMapping={savePropertyMapping}
          />
        )}
      </Stack>
    </Paper>
  );
};

export default RevitToIfcSettings;

const PropertyMappingSettings: React.FC<{
  propertyMapping: IBatchjob.IPropertyMapping;
  saveProperyMapping: (propetyMapping: IBatchjob.IPropertyMapping) => void;
}> = ({ propertyMapping, saveProperyMapping }) => {
  const [warningFile, setWarningFile] = React.useState<boolean>(false);
  const uploadFile = (event) => {
    setWarningFile(false);
    const files = event.target.files,
      f = files[0];

    if (f.size > 20000) {
      return setWarningFile(true);
    }
    const reader = new FileReader();
    reader.onload = function (e) {
      let data = e.target.result;
      if (isUploadValid(data) === false) {
        setWarningFile(true);
      } else {
        saveProperyMapping({
          fileName: f.fileName,
          content: data as string,
        });
      }
    };
    reader.readAsText(f);
  };

  const isUploadValid = (content: any): boolean => {
    if (typeof content !== "string") {
      return false;
    }
    return content.includes("PropertySet:") !== false;
  };
  return (
    <>
      {warningFile && (
        <Alert color="yellow" variant="light">
          Filens innhold ser ikke ut til å være en korrekt egenskapskartleggingsfil fra Revit
        </Alert>
      )}
      <div className="d-flex flex-column">
        <span>Laste opp ny (max 20kb)</span>
        <input accept=".txt" type="file" onChange={(event) => uploadFile(event)} />
      </div>
      <VSpace />
      {propertyMapping != null && (
        <>
          Fil: {propertyMapping.fileName}
          <pre className="bg-light p-2">{propertyMapping.content}</pre>
        </>
      )}
    </>
  );
};

const JobParamsSettings: React.FC<{ jobParams: IBatchjob.IIFCExportJobParams; save: (value, key: string) => void }> = ({
  jobParams,
  save,
}) => {
  const [showAdvanced, setShowAdvanced] = React.useState<boolean>(false);
  const IFC_VERSIONS = [IfcVersionEnums.IFC2X3, IfcVersionEnums.IFC4];

  return (
    <div>
      <Select
        data={IFC_VERSIONS.map((ifcVersion) => ({ value: ifcVersion, label: ifcVersion }))}
        value={jobParams?.[RevitIfcParamsEnums.VersionClass] ?? null}
        onChange={(value) => (value != null ? save(value, `jobParams.${RevitIfcParamsEnums.VersionClass}`) : undefined)}
        style={{ minWidth: "200px", maxWidth: "280px" }}
      />
      <Button variant="subtle" px={0} onClick={() => setShowAdvanced(!showAdvanced)}>
        {showAdvanced === false ? (
          <span>Vise avanserte innstillinger</span>
        ) : (
          <span>Skjul avanserte innstillinger</span>
        )}
      </Button>
      {showAdvanced === true && (
        <>
          {Object.keys(jobParams.Options)
            .filter(
              (settingKey) =>
                settingKey !== RevitIfcParamsEnums.ExportIFCCommonPropertySets &&
                settingKey !== RevitIfcParamsEnums.ExportUserDefinedParameterMapping,
            )
            .filter((settingKey) => typeof jobParams.Options[settingKey] === "boolean")
            .map((settingKey, index) => (
              <div key={index} className="mb-2">
                <SwitchWithTitle
                  title={settingKey}
                  onSave={(bool) => save(bool, `jobParams.Options.${settingKey}`)}
                  checked={jobParams.Options[settingKey]}
                />
              </div>
            ))}
        </>
      )}
    </div>
  );
};
